import React, { useEffect, useMemo, useState } from 'react';
import {
  AppBar, Box, Button, Card, CardContent, Checkbox, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControl, FormControlLabel, Grid, IconButton, InputLabel, MenuItem,
  Paper, Select, Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs, TextField, Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { DeleteOutline, Refresh, Send, Settings, Link as LinkIcon, PictureAsPdf } from '@material-ui/icons';
import api from '../../../services/api';
import { useLocation } from 'react-router-dom';

const formatarDataBR = (valor) => {
  if (!valor) return "";
  const texto = String(valor);
  const match = texto.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : texto;
};


const PAX_TYPES = [
  { id: 1, label: 'ADT' },
  { id: 2, label: 'CHD' },
  { id: 3, label: 'INF' },
  { id: 5, label: 'SEN' }
];

const useStyles = makeStyles(theme => ({
  root: { padding: theme.spacing(3) },
  toolbar: { marginBottom: theme.spacing(2) },
  card: { marginBottom: theme.spacing(2) },
  tableWrap: { overflowX: 'auto' },
  statusOk: { color: '#2e7d32', fontWeight: 600 },
  statusWarn: { color: '#ed6c02', fontWeight: 600 },
  statusError: { color: '#d32f2f', fontWeight: 600 },
  monospace: { fontFamily: 'monospace' }
}));

function todayISO() {
  const d = new Date();
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 10);
}

function errorMessage(error) {
  return error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Erro inesperado.';
}

function OrbitIntegration() {
  const classes = useStyles();
  const location = useLocation();
  const parqueRota = location.pathname.toLowerCase().includes('/acquamotion') ? 'acquamotion' : 'snowland';
  const nomeParque = parqueRota === 'acquamotion' ? 'AcquaMotion' : 'Snowland';
  const pertenceAoParque = (config) => {
    const texto = `${config.descricao || ''} ${config.enterprise || ''}`.toLowerCase();
    return parqueRota === 'acquamotion' ? /acqua[ -]?motion/.test(texto) : /snow[ -]?land/.test(texto);
  };
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [configs, setConfigs] = useState([]);
  const [services, setServices] = useState([]);
  const [products, setProducts] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [selectedConfig, setSelectedConfig] = useState('');
  const [selectedService, setSelectedService] = useState('');
  const [dateIni, setDateIni] = useState(todayISO());
  const [dateFim, setDateFim] = useState(todayISO());
  const [configDialog, setConfigDialog] = useState(false);
  const [editingConfigId, setEditingConfigId] = useState(0);
  const [configForm, setConfigForm] = useState({
    descricao: '', enterprise: '', baseurl: 'https://orbit-parceiros.gramadoparks.com/api/partner/v1',
    publickey: '', secretkey: '', webhooksecret: '', ambiente: 'SANDBOX', ativo: true, usacontatopadrao: false, emailpadrao: '', fonepadrao: ''
  });
  const [mapForm, setMapForm] = useState({ 1: '', 2: '', 3: '', 5: '' });

  const configsParque = useMemo(() => configs.filter(pertenceAoParque), [configs, parqueRota]);

  const selectedConfigObj = useMemo(
    () => configsParque.find(c => String(c.id) === String(selectedConfig)),
    [configsParque, selectedConfig]
  );

  async function loadConfigs() {
    const response = await api.get('/orbit/config', { params: { parque: parqueRota } });
    const data = response.data?.data || [];
    setConfigs(data);
    const doParque = data.filter(pertenceAoParque);
    setSelectedConfig(atual => doParque.some(c => String(c.id) === String(atual))
      ? atual : (doParque.length ? String(doParque[0].id) : ''));
  }

  async function loadServices() {
    const response = await api.get('/servico/active/true?limit=1000');
    const data = response.data?.data || [];
    setServices(data);
  }

  async function loadMappings(configId = selectedConfig, serviceId = selectedService) {
    if (!configId) return;
    const response = await api.get('/orbit/mappings', { params: { idconfig: configId } });
    const data = response.data?.data || [];
    setMappings(data);
    const service = serviceId && data.some(m => String(m.codservico) === String(serviceId))
      ? String(serviceId)
      : (!serviceId && data.length ? String(data[0].codservico) : String(serviceId || ''));
    if (service !== String(serviceId || '')) setSelectedService(service);
    const next = { 1: '', 2: '', 3: '', 5: '' };
    data.forEach(m => {
      if (String(m.codservico) === service && m.ativo !== 'N') next[m.tipopax] = String(m.productid);
    });
    setMapForm(next);
    return data;
  }

  async function loadProducts(configId = selectedConfig, silent = false) {
    if (!configId) return;
    setLoading(true);
    try {
      const response = await api.get(`/orbit/config/${configId}/products`, { params: { type: 'ingresso' } });
      setProducts(response.data?.data || []);
      if (!silent) setMessage('Catálogo Orbit atualizado.');
    } catch (error) {
      setProducts([]);
      setMessage(errorMessage(error));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    Promise.all([loadConfigs(), loadServices()]).catch(error => setMessage(errorMessage(error)));
  }, []);

  useEffect(() => {
    setReservations([]);
    setProducts([]);
    setMappings([]);
    setSelectedService('');
  }, [selectedConfig, parqueRota]);

  useEffect(() => {
    if (!selectedConfig) return;
    setProducts([]);
    setSelectedService('');
    loadMappings(selectedConfig, '').then(data => {
      if (data && data.length) loadProducts(selectedConfig, true);
    }).catch(error => setMessage(errorMessage(error)));
  }, [selectedConfig, parqueRota]);

  useEffect(() => {
    if (selectedConfig && selectedService) {
      loadMappings(selectedConfig, selectedService).catch(error => setMessage(errorMessage(error)));
    }
  }, [selectedService]);

  useEffect(() => {
    const doParque = configs.filter(pertenceAoParque);
    setSelectedConfig(atual => doParque.some(c => String(c.id) === String(atual))
      ? atual : (doParque.length ? String(doParque[0].id) : ''));
  }, [parqueRota, configs]);

  function newConfig() {
    setEditingConfigId(0);
    setConfigForm({
      descricao: nomeParque, enterprise: parqueRota, baseurl: 'https://orbit-parceiros.gramadoparks.com/api/partner/v1',
      publickey: '', secretkey: '', webhooksecret: '', ambiente: 'SANDBOX', ativo: true, usacontatopadrao: false, emailpadrao: '', fonepadrao: ''
    });
    setConfigDialog(true);
  }

  function editConfig(cfg) {
    if (!pertenceAoParque(cfg)) { setMessage('Configuração pertence a outro parque.'); return; }
    setEditingConfigId(cfg.id);
    setConfigForm({
      descricao: cfg.descricao || '', enterprise: cfg.enterprise || '', baseurl: cfg.baseurl || '',
      publickey: cfg.publickey || '', secretkey: '********', webhooksecret: cfg.temwebhooksecret === 'S' ? '********' : '',
      ambiente: cfg.ambiente || 'SANDBOX', ativo: cfg.ativo !== 'N',
      usacontatopadrao: cfg.usacontatopadrao === 'S', emailpadrao: cfg.emailpadrao || '', fonepadrao: cfg.fonepadrao || ''
    });
    setConfigDialog(true);
  }

  async function saveConfig() {
    if (!pertenceAoParque(configForm)) {
      setMessage(`A configuração deve identificar o parque ${nomeParque} na descrição ou no campo Enterprise.`);
      return;
    }
    setLoading(true);
    try {
      const payload = { ...configForm, ativo: configForm.ativo ? 'S' : 'N', usacontatopadrao: configForm.usacontatopadrao ? 'S' : 'N', codempresa: Number(sessionStorage.getItem('codEmpresa') || 0), parqueTela: parqueRota };
      if (editingConfigId) await api.put(`/orbit/config/${editingConfigId}`, payload);
      else await api.post('/orbit/config', payload);
      setConfigDialog(false);
      await loadConfigs();
      setMessage('Configuração salva com sucesso.');
    } catch (error) {
      setMessage(errorMessage(error));
    } finally {
      setLoading(false);
    }
  }

  async function testConfig(id) {
    if (!configsParque.some(c => String(c.id) === String(id))) return;
    setLoading(true);
    try {
      const response = await api.post(`/orbit/config/${id}/test`);
      setMessage(`${response.data.message} ${response.data.products?.length || 0} produto(s) retornado(s).`);
    } catch (error) {
      setMessage(errorMessage(error));
    } finally { setLoading(false); }
  }

  async function deleteConfig(id) {
    if (!configsParque.some(c => String(c.id) === String(id))) return;
    if (!window.confirm('Excluir esta configuração Orbit?')) return;
    try {
      await api.delete(`/orbit/config/${id}`);
      if (String(selectedConfig) === String(id)) setSelectedConfig('');
      await loadConfigs();
      setMessage('Configuração excluída.');
    } catch (error) { setMessage(errorMessage(error)); }
  }

  async function saveMappings() {
    if (!selectedConfig || !selectedService) {
      setMessage('Selecione a configuração e o serviço Sisare.');
      return;
    }
    setLoading(true);
    try {
      for (const pax of PAX_TYPES) {
        const productId = Number(mapForm[pax.id] || 0);
        const existing = mappings.find(m => String(m.codservico) === String(selectedService) && Number(m.tipopax) === pax.id);
        if (productId) {
          const payload = { idconfigorbit: Number(selectedConfig), codservico: Number(selectedService), tipopax: pax.id, productid: productId, ativo: 'S' };
          if (existing) await api.put(`/orbit/mappings/${existing.id}`, payload);
          else await api.post('/orbit/mappings', payload);
        } else if (existing) {
          await api.delete(`/orbit/mappings/${existing.id}`);
        }
      }
      await loadMappings();
      setMessage('Vínculos de produtos salvos.');
    } catch (error) { setMessage(errorMessage(error)); }
    finally { setLoading(false); }
  }

  async function searchReservations() {
    if (!selectedConfig || !selectedService) {
      setMessage('Selecione configuração e serviço.');
      return;
    }
    setLoading(true);
    try {
      const response = await api.get('/orbit/reservations', {
        params: { idconfig: selectedConfig, codservico: selectedService, dataini: dateIni, datafim: dateFim }
      });
      setReservations(response.data?.data || []);
      setMessage(`${response.data?.data?.length || 0} reserva(s) encontrada(s).`);
    } catch (error) {
      setReservations([]);
      setMessage(errorMessage(error));
    } finally { setLoading(false); }
  }

  async function sendReservation(row) {
    if (!row.validacao?.ok) {
      setMessage('Esta reserva possui pendências de cadastro ou de mapeamento.');
      return;
    }
    if (!window.confirm(`Enviar OS ${row.nroos} / item ${row.coditemos} para a Orbit?`)) return;
    setLoading(true);
    try {
      await api.post('/orbit/orders', { idconfig: Number(selectedConfig), coditemos: row.coditemos, codfamilia: row.codfamilia || 0 });
      setMessage('Venda enviada à Orbit.');
      await searchReservations();
    } catch (error) { setMessage(errorMessage(error)); }
    finally { setLoading(false); }
  }

  async function refreshOrder(row) {
    const localId = row.integracao?.id;
    if (!localId) return;
    setLoading(true);
    try {
      await api.post(`/orbit/orders/${localId}/refresh`);
      setMessage('Situação Orbit atualizada.');
      await searchReservations();
    } catch (error) { setMessage(errorMessage(error)); }
    finally { setLoading(false); }
  }

  async function openVoucher(row) {
    try {
      const response = await api.get(`/orbit/orders/${row.integracao.id}/voucher`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (error) { setMessage(errorMessage(error)); }
  }

  function validationText(row) {
    const v = row.validacao || {};
    const parts = [];
    if (v.mapeamentosfaltantes?.length) parts.push(`sem produto: ${v.mapeamentosfaltantes.join(', ')}`);
    if (v.paxdadosfaltantes?.length) parts.push(`${v.paxdadosfaltantes.length} pax sem documento/nascimento`);
    if (v.titularfonefaltante) parts.push('titular sem telefone');
    if (v.titularemailfaltante) parts.push('titular sem e-mail');
    return parts.join('; ') || 'OK';
  }

  return (
    <div className={classes.root}>
      <Typography variant="h4" gutterBottom>Integração Gramado Parks / Orbit — {nomeParque}</Typography>
      <Typography variant="body2" color="textSecondary" className={classes.toolbar}>
        Mantém o serviço único do Sisare e converte ADT/CHD/INF/SEN nos produtos correspondentes da Orbit.
      </Typography>

      {!configsParque.length && <Paper style={{ padding: 12, marginBottom: 16 }}><Typography color="textSecondary">Nenhuma configuração {nomeParque} cadastrada. Acesse Configurações para cadastrar o parque e suas credenciais Orbit.</Typography></Paper>}
      {message && <Paper style={{ padding: 12, marginBottom: 16 }}><Typography>{message}</Typography></Paper>}
      {loading && <Box display="flex" alignItems="center" mb={2}><CircularProgress size={22} /><Box ml={1}>Processando...</Box></Box>}

      <AppBar position="static" color="default">
        <Tabs value={tab} onChange={(_e, v) => setTab(v)} indicatorColor="primary" textColor="primary">
          <Tab icon={<Send />} label="Reservas / Vendas" />
          <Tab icon={<LinkIcon />} label="Produtos por tipo de pax" />
          <Tab icon={<Settings />} label="Configurações" />
        </Tabs>
      </AppBar>

      {tab === 0 && <Box mt={2}>
        <Card className={classes.card}><CardContent>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} md={3}><FormControl fullWidth><InputLabel>Integração / Parque</InputLabel><Select value={selectedConfig} onChange={e => setSelectedConfig(e.target.value)}>{configsParque.map(c => <MenuItem key={c.id} value={String(c.id)}>{c.descricao}</MenuItem>)}</Select></FormControl></Grid>
            <Grid item xs={12} md={3}><FormControl fullWidth><InputLabel>Serviço Sisare</InputLabel><Select value={selectedService} onChange={e => setSelectedService(e.target.value)}>{services.map(s => <MenuItem key={s.codservico} value={String(s.codservico)}>{s.codservico} - {s.descservico}</MenuItem>)}</Select></FormControl></Grid>
            <Grid item xs={6} md={2}><TextField fullWidth label="Data inicial" type="date" value={dateIni} onChange={e => setDateIni(e.target.value)} InputLabelProps={{ shrink: true }} /></Grid>
            <Grid item xs={6} md={2}><TextField fullWidth label="Data final" type="date" value={dateFim} onChange={e => setDateFim(e.target.value)} InputLabelProps={{ shrink: true }} /></Grid>
            <Grid item xs={12} md={2}><Button fullWidth variant="contained" color="primary" onClick={searchReservations}>Pesquisar</Button></Grid>
          </Grid>
        </CardContent></Card>

        <Paper className={classes.tableWrap}>
          <Table size="small">
            <TableHead><TableRow><TableCell>OS</TableCell><TableCell>Item</TableCell><TableCell>Data</TableCell><TableCell>Titular</TableCell><TableCell>ADT</TableCell><TableCell>CHD</TableCell><TableCell>INF</TableCell><TableCell>SEN</TableCell><TableCell>Validação</TableCell><TableCell>Orbit</TableCell><TableCell align="right">Ações</TableCell></TableRow></TableHead>
            <TableBody>{reservations.map(row => {
              const integ = row.integracao;
              const issued = integ?.emissionstatus === 'issued';
              return <TableRow key={`${row.coditemos}-${row.codfamilia}`}>
                <TableCell>{row.nroos}</TableCell><TableCell>{row.coditemos}</TableCell><TableCell>{formatarDataBR(row.dataservico)}</TableCell>
                <TableCell>{row.titular?.nome}</TableCell><TableCell>{row.qtdeadt}</TableCell><TableCell>{row.qtdechd}</TableCell><TableCell>{row.qtdeinf}</TableCell><TableCell>{row.qtdesen}</TableCell>
                <TableCell className={row.validacao?.ok ? classes.statusOk : classes.statusError}>{validationText(row)}</TableCell>
                <TableCell><div>{integ?.orbitordercode || 'Não enviado'}</div><small className={integ?.errorcode ? classes.statusError : (issued ? classes.statusOk : classes.statusWarn)}>{integ?.errorcode ? `${integ.errorcode}: ${integ.errormessage || ''}` : (integ ? `${integ.status || ''} / ${integ.emissionstatus || ''}` : '')}</small></TableCell>
                <TableCell align="right">
                  {!integ?.orbitorderid && <IconButton title="Enviar" onClick={() => sendReservation(row)} disabled={!row.validacao?.ok}><Send /></IconButton>}
                  {integ?.orbitorderid && <IconButton title="Atualizar status" onClick={() => refreshOrder(row)}><Refresh /></IconButton>}
                  {integ?.orbitorderid && (integ.status === 'paid' || issued) && <IconButton title="Voucher" onClick={() => openVoucher(row)}><PictureAsPdf /></IconButton>}
                </TableCell>
              </TableRow>;
            })}</TableBody>
          </Table>
        </Paper>
      </Box>}

      {tab === 1 && <Box mt={2}>
        <Card className={classes.card}><CardContent>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} md={4}><FormControl fullWidth><InputLabel>Integração / Parque</InputLabel><Select value={selectedConfig} onChange={e => { setSelectedService(''); setSelectedConfig(e.target.value); setProducts([]); }}>{configsParque.map(c => <MenuItem key={c.id} value={String(c.id)}>{c.descricao}</MenuItem>)}</Select></FormControl></Grid>
            <Grid item xs={12} md={5}><FormControl fullWidth><InputLabel>Serviço Sisare</InputLabel><Select value={selectedService} onChange={e => setSelectedService(e.target.value)}>{services.map(s => <MenuItem key={s.codservico} value={String(s.codservico)}>{s.codservico} - {s.descservico}</MenuItem>)}</Select></FormControl></Grid>
            <Grid item xs={12} md={3}><Button variant="outlined" color="primary" fullWidth onClick={() => loadProducts()}>Carregar catálogo Orbit</Button></Grid>
          </Grid>
          <Box mt={3}><Grid container spacing={2}>{PAX_TYPES.map(p => <Grid item xs={12} md={3} key={p.id}><FormControl fullWidth><InputLabel>{p.label}</InputLabel><Select value={mapForm[p.id] || ''} onChange={e => setMapForm({ ...mapForm, [p.id]: e.target.value })}><MenuItem value=""><em>Não utilizado</em></MenuItem>{products.map(prod => <MenuItem key={prod.id} value={String(prod.id)}>{prod.id} - {prod.short_name || prod.name}</MenuItem>)}</Select></FormControl></Grid>)}</Grid></Box>
          <Box mt={3}><Button variant="contained" color="primary" onClick={saveMappings}>Salvar vínculos</Button></Box>
        </CardContent></Card>
        <Typography variant="body2" color="textSecondary">O item da OS continua sendo um único CODSERVICO. Estes vínculos são usados somente para montar os items[] do pedido Orbit.</Typography>
      </Box>}

      {tab === 2 && <Box mt={2}>
        <Box mb={2}><Button variant="contained" color="primary" onClick={newConfig}>Nova configuração</Button></Box>
        <Paper className={classes.tableWrap}><Table size="small"><TableHead><TableRow><TableCell>Descrição</TableCell><TableCell>Parque</TableCell><TableCell>Ambiente</TableCell><TableCell>URL</TableCell><TableCell>PK</TableCell><TableCell>SK</TableCell><TableCell>Webhook</TableCell><TableCell>Ativo</TableCell><TableCell align="right">Ações</TableCell></TableRow></TableHead><TableBody>{configsParque.map(c => <TableRow key={c.id}><TableCell>{c.descricao}</TableCell><TableCell>{c.enterprise}</TableCell><TableCell>{c.ambiente}</TableCell><TableCell className={classes.monospace}>{c.baseurl}</TableCell><TableCell className={classes.monospace}>{c.publickey}</TableCell><TableCell>{c.temsecret === 'S' ? 'Configurado' : 'Não'}</TableCell><TableCell>{c.temwebhooksecret === 'S' ? 'Configurado' : 'Não'}</TableCell><TableCell>{c.ativo === 'N' ? 'Não' : 'Sim'}</TableCell><TableCell align="right"><Button size="small" onClick={() => testConfig(c.id)}>Testar</Button><Button size="small" onClick={() => editConfig(c)}>Editar</Button><IconButton size="small" onClick={() => deleteConfig(c.id)}><DeleteOutline /></IconButton></TableCell></TableRow>)}</TableBody></Table></Paper>
        {selectedConfigObj && <Box mt={2}><Typography variant="body2"><strong>URL para cadastrar no painel Orbit:</strong> {`${window.location.origin.replace(/\/$/, '')}/api/orbit/webhook/${selectedConfigObj.id}`} (ajuste o prefixo conforme a publicação da sua API).</Typography></Box>}
      </Box>}

      <Dialog open={configDialog} onClose={() => setConfigDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingConfigId ? 'Editar configuração Orbit' : 'Nova configuração Orbit'}</DialogTitle>
        <DialogContent><Grid container spacing={2}>
          <Grid item xs={12} md={6}><TextField fullWidth label="Descrição" value={configForm.descricao} onChange={e => setConfigForm({ ...configForm, descricao: e.target.value })} /></Grid>
          <Grid item xs={12} md={6}><TextField fullWidth label="Parque / Enterprise" value={configForm.enterprise} onChange={e => setConfigForm({ ...configForm, enterprise: e.target.value })} /></Grid>
          <Grid item xs={12}><TextField fullWidth label="Base URL" value={configForm.baseurl} onChange={e => setConfigForm({ ...configForm, baseurl: e.target.value })} helperText="Ex.: https://orbit-parceiros.gramadoparks.com/api/partner/v1" /></Grid>
          <Grid item xs={12} md={6}><TextField fullWidth label="Public Key (pk_)" value={configForm.publickey} onChange={e => setConfigForm({ ...configForm, publickey: e.target.value })} /></Grid>
          <Grid item xs={12} md={6}><TextField fullWidth type="password" label="Secret Key (sk_)" value={configForm.secretkey} onChange={e => setConfigForm({ ...configForm, secretkey: e.target.value })} /></Grid>
          <Grid item xs={12} md={6}><TextField fullWidth type="password" label="Segredo do Webhook" value={configForm.webhooksecret} onChange={e => setConfigForm({ ...configForm, webhooksecret: e.target.value })} /></Grid>
          <Grid item xs={12} md={3}><FormControl fullWidth><InputLabel>Ambiente</InputLabel><Select value={configForm.ambiente} onChange={e => setConfigForm({ ...configForm, ambiente: e.target.value })}><MenuItem value="SANDBOX">Sandbox</MenuItem><MenuItem value="PRODUCAO">Produção</MenuItem></Select></FormControl></Grid>
          <Grid item xs={12} md={3}><FormControlLabel control={<Checkbox checked={configForm.ativo} onChange={e => setConfigForm({ ...configForm, ativo: e.target.checked })} color="primary" />} label="Ativo" /></Grid>
          <Grid item xs={12}><Typography variant="subtitle1" style={{ marginTop: 12 }}>Contato padrão do comprador — {nomeParque}</Typography></Grid>
          <Grid item xs={12}><FormControlLabel control={<Checkbox color="primary" checked={!!configForm.usacontatopadrao} onChange={e => setConfigForm({ ...configForm, usacontatopadrao: e.target.checked })} />} label="Utilizar contato padrão quando não informado na OS" /></Grid>
          <Grid item xs={12} md={6}><TextField fullWidth label="E-mail padrão do comprador" type="email" disabled={!configForm.usacontatopadrao} value={configForm.emailpadrao || ''} onChange={e => setConfigForm({ ...configForm, emailpadrao: e.target.value })} /></Grid>
          <Grid item xs={12} md={6}><TextField fullWidth label="Telefone padrão do comprador" disabled={!configForm.usacontatopadrao} value={configForm.fonepadrao || ''} onChange={e => setConfigForm({ ...configForm, fonepadrao: e.target.value })} /></Grid>
          <Grid item xs={12}><Typography variant="caption" color="textSecondary">Os dados da OS têm prioridade. Os padrões são utilizados individualmente somente quando o contato estiver ausente.</Typography></Grid>
        </Grid></DialogContent>
        <DialogActions><Button onClick={() => setConfigDialog(false)}>Cancelar</Button><Button color="primary" variant="contained" onClick={saveConfig}>Salvar</Button></DialogActions>
      </Dialog>
    </div>
  );
}

export default OrbitIntegration;
